/* eslint-disable unicorn/filename-case -- TODO rename later */
import { ControlTaggingStatus } from "@istari/istari-client";
import React, { useEffect, useRef, useState } from "react";
import {
  components,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";

import { LabeledControlTag } from "~/api/useControlTags";
import { SelectMenu } from "~/components/__deprecated";
import { cn } from "~/utils";

type InfoSecTagsMenuProps = {
  options: LabeledControlTag[] | undefined;
  selectedOptions?: LabeledControlTag[];
  onSelectOptions: (tag: LabeledControlTag[]) => void;
  container?: HTMLElement | null;
};

const Option = (props: OptionProps<LabeledControlTag, true>) => {
  return (
    <div className="grid grid-cols-1 text-xs">
      <components.Option {...props}>
        <div className="grid">
          <div
            className="font-medium"
            style={{
              color: props.data.color,
            }}
          >
            {props.data.label}
          </div>
          <div className="text-muted-foreground leading-relaxed">{props.data.description}</div>
        </div>
      </components.Option>
    </div>
  );
};

const MultiValueLabel = (props: MultiValueGenericProps<LabeledControlTag, true>) => {
  return (
    <components.MultiValueLabel
      {...props}
      innerProps={{
        ...props.innerProps,
        className: cn(
          "!text-white truncate relative z-10 relative flex max-w-32 items-center truncate overflow-hidden px-2.5 py-1 text-xs font-medium",
        ),
      }}
    />
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps<LabeledControlTag, true>) => {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        ...props.innerProps,
        className: "text-white hover:text-white/80 transition-colors duration-150 relative z-10",
        "aria-label": `Remove ${props.data.label}`,
        style: {
          borderTopLeftRadius: "0",
          borderBottomLeftRadius: "0",
        },
      }}
    />
  );
};
const ValueContainer = React.forwardRef<HTMLDivElement, ValueContainerProps<LabeledControlTag, true>>((props, ref) => {
  return (
    <components.ValueContainer
      {...props}
      innerProps={
        {
          ...props.innerProps,
          ref,
          "data-slot": "value-container",
        } as React.HTMLAttributes<HTMLDivElement>
      }
    />
  );
});

const scrollToChip = (container: HTMLDivElement, chipElements: Element[], targetIndex: number) => {
  const targetChip = chipElements[targetIndex] as HTMLElement;
  if (!targetChip) return;

  const chipLeft = targetChip.offsetLeft;
  const chipRight = chipLeft + targetChip.offsetWidth;
  const containerScrollLeft = container.scrollLeft;
  const containerWidth = container.clientWidth;

  // If chip is to the left of visible area, scroll left to show it
  if (chipLeft < containerScrollLeft) {
    container.scrollLeft = chipLeft; // eslint-disable-line no-param-reassign
  }
  // If chip is to the right of visible area, scroll right to show it
  else if (chipRight > containerScrollLeft + containerWidth) {
    container.scrollLeft = chipRight - containerWidth; // eslint-disable-line no-param-reassign
  }
};

/**
 * A Multi-Value Select Menu for the InfoSec Tags with keyboard navigation support.
 *
 * @todo Fix some scroll-x positioning issues. Best would be to use a new multi-select library——long overdue.
 * @todo Fix missing dark mode styles
 */
const InfoSecTagsMenu = ({ options, selectedOptions, onSelectOptions, container }: InfoSecTagsMenuProps) => {
  const [selectedOpts, setSelectedOpts] = useState(selectedOptions || []);
  const valueContainerRef = useRef<HTMLDivElement>(null);
  const cursorPositionRef = useRef<number>(0);

  // Reset cursor position when selected options change
  useEffect(() => {
    cursorPositionRef.current = Math.min(cursorPositionRef.current, Math.max(0, selectedOpts.length - 1));
  }, [selectedOpts.length]);

  const validOptions = (options || [])
    .filter((it) => !it.upstreamRemoteInfo)
    .filter((it) => it.status == ControlTaggingStatus.Active);

  return (
    <SelectMenu
      components={{
        MultiValueLabel,
        MultiValueRemove,
        Option,
        ValueContainer: React.forwardRef<HTMLDivElement, ValueContainerProps<LabeledControlTag, true>>((props, _) => (
          <ValueContainer {...props} ref={valueContainerRef} />
        )),
      }}
      isClearable={false}
      closeMenuOnSelect={false}
      openMenuOnClick
      isMulti
      options={validOptions}
      placeholder="Select a control tag..."
      value={selectedOpts}
      onChange={(values) => {
        setSelectedOpts(values as LabeledControlTag[]);
        onSelectOptions(values as LabeledControlTag[]);
      }}
      onKeyDown={(e) => {
        const container = valueContainerRef.current;
        if (!container) return;

        const chipElements = Array.from(container.children).filter(
          (child) =>
            child.getAttribute("data-slot") !== "input-container" && child.getAttribute("data-slot") !== "placeholder",
        );

        if (chipElements.length === 0) return;

        if (e.key === "ArrowLeft") {
          cursorPositionRef.current = Math.max(0, cursorPositionRef.current - 1);
          scrollToChip(container, chipElements, cursorPositionRef.current);
        } else if (e.key === "ArrowRight") {
          cursorPositionRef.current = Math.min(chipElements.length - 1, cursorPositionRef.current + 1);
          scrollToChip(container, chipElements, cursorPositionRef.current);
        }
      }}
      menuPortalTarget={container}
      styles={{
        control: (base) => ({
          ...base,
          minHeight: "2.25rem",
          height: "2.25rem",
          minWidth: "200px",
          maxWidth: "300px",
          borderRadius: "6px",
          padding: "4px",
          position: "relative",
        }),
        valueContainer: (base, state) => ({
          ...base,
          padding: state.hasValue ? "0" : "0 12px",
          gap: "4px",
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          maxHeight: "2rem",
          alignItems: "center",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }),
        input: (base) => ({
          ...base,
          margin: "0",
          fontSize: "0.875rem",
          lineHeight: "1",
        }),
        multiValue: (base, state) => ({
          ...base,
          margin: "0",
          minWidth: "auto",
          flexShrink: 0,
          borderRadius: "5px",
          overflow: "hidden",
          transition: "all 0.15s ease-out",
          ...(state.data.color && { backgroundColor: state.data.color }),
        }),
        menu: (base) => ({
          ...base,
          minWidth: "max(300px, 100%)",
          maxWidth: "400px",
          zIndex: 9999,
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: "200px",
          overflowY: "auto",
        }),
        option: (base, state) => ({
          ...base,
          padding: "0",
          backgroundColor: state.isFocused ? "hsl(var(--muted))" : "transparent",
          color: state.isFocused ? "hsl(var(--foreground))" : base.color,
          "&:active": {
            backgroundColor: "hsl(var(--accent))",
          },
        }),
        menuPortal: (base) => {
          if (!container) {
            return base;
          }

          const containerRect = container.getBoundingClientRect();
          const containerScrollLeft = container.scrollLeft ?? 0;
          const containerScrollTop = container.scrollTop ?? 0;

          // react-select measures menu position in viewport coordinates; converting to container-relative coordinates keeps the dropdown aligned with the trigger inside the dialog portal.
          const getNumeric = (value: unknown) => {
            if (typeof value === "number") {
              return value;
            }
            if (typeof value === "string") {
              const parsed = parseFloat(value);
              return Number.isNaN(parsed) ? 0 : parsed;
            }
            return 0;
          };

          const baseLeft = getNumeric(base.left);
          const baseTop = getNumeric(base.top);

          return {
            ...base,
            position: "absolute",
            zIndex: base.zIndex ?? 9999,
            left: baseLeft - containerRect.left + containerScrollLeft,
            top: baseTop - containerRect.top + containerScrollTop,
          };
        },
        placeholder: (base) => ({
          ...base,
          color: "var(--muted-foreground)",
          fontSize: "0.875rem",
          margin: "0",
        }),
        indicatorSeparator: (base) => ({
          ...base,
          display: "none",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          padding: "4px",
          color: "var(--muted-foreground)",
          "&:hover": {
            color: "var(--foreground)",
          },
        }),
      }}
    />
  );
};

export default InfoSecTagsMenu;
